import { select } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import type { PopperProps } from 'react-popper';
import { Popper } from 'react-popper';
import { useSelector } from 'react-redux';

// Use PopperProps after material-ui update
interface OwnProps {
  createPortal?: boolean;
  isOpen: boolean;
  onClose?(): void;
}

type Props = OwnProps & PopperProps<object>;

const Tooltip: React.FC<Props> = ({ children, isOpen, createPortal = true, onClose, ...rest }) => {
  const node = useRef<HTMLElement>(null);
  const isMobile = useSelector(select.device.isMobile);

  // TODO: convert to usePopper hook
  const renderTooltip = useCallback(
    () => (
      <Popper {...rest} innerRef={node}>
        {children}
      </Popper>
    ),
    [children, rest]
  );

  const closeTooltip = useCallback(
    (e: TouchEvent | MouseEvent) => {
      if (!isOpen) {
        return;
      }

      const popper = node.current;

      if (onClose && popper && !popper.contains(e.target as Node)) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    if (canUseDOM && onClose) {
      if (isMobile) {
        window.addEventListener('touchend', closeTooltip, false);
      } else {
        window.addEventListener('click', closeTooltip, false);
      }
    }
    return () => {
      if (canUseDOM && onClose) {
        if (isMobile) {
          window.removeEventListener('touchend', closeTooltip, false);
        } else {
          window.removeEventListener('click', closeTooltip, false);
        }
      }
    };
  }, [closeTooltip, isMobile, onClose]);

  if (!isOpen) {
    return null;
  }

  if (createPortal) {
    return canUseDOM
      ? ReactDOM.createPortal(renderTooltip(), document.getElementById('root') as HTMLElement)
      : null;
  }
  return renderTooltip();
};

export default Tooltip;
